/**
 * Geometria das barras de carga do time.
 *
 * A trilha não vai de 0 a 100% da largura: 100% da capacidade individual cai sobre o tique,
 * em {@link CAP_TICK}, sobrando um pedaço à direita para o estouro aparecer como estouro.
 * Sem essa folga, quem está em 100% e quem está em 160% desenham a mesma barra cheia.
 */

/** Posição do tique de 100% da capacidade, em % da largura da trilha. */
export const CAP_TICK = 91.7;

export interface LoadBar {
  /** Percentual da capacidade individual. */
  pct: number;
  /** Largura do preenchimento, em % da trilha, já saturada em 100. */
  width: number;
  /** Passou da capacidade — 100% cravado ainda não é estouro. */
  over: boolean;
}

/**
 * @param hours horas da série (planejadas ou apontadas)
 * @param capacity capacidade individual do período
 * @param knownPct percentual já calculado pelo backend; quando informado, prevalece sobre
 *   `hours / capacity` porque o cálculo do capacity considera overrides de dia e feriados.
 */
/** As duas séries desenhadas sobre a mesma capacidade. */
export type SeriesKind = "planned" | "worked";

/**
 * Cor de cada série. A cor é fixa por série e não pela posição, porque a ordem das barras
 * muda entre as lentes (na semana o planejado vem primeiro; no mês, o apontado) — sem uma
 * âncora semântica, azul significaria coisas diferentes em duas telas vizinhas.
 *
 * Estourar planejando é vermelho: ainda dá para redistribuir.
 * Estourar apontando é âmbar: já é hora extra, não há o que redistribuir.
 */
export function barTone(kind: SeriesKind, over: boolean): string {
  if (kind === "planned") {
    return over
      ? "bg-gradient-to-r from-[#E05252] to-red-400"
      : "bg-gradient-to-r from-dv-blue-500 to-dv-cyan-400";
  }
  return over
    ? "bg-gradient-to-r from-amber-500 to-amber-400"
    : "bg-gradient-to-r from-emerald-500 to-emerald-400";
}

export function loadBar(hours: number, capacity: number, knownPct?: number): LoadBar {
  if (capacity <= 0) return { pct: 0, width: 0, over: false };

  const pct = knownPct ?? (hours / capacity) * 100;
  return {
    pct,
    width: Math.min(100, pct * (CAP_TICK / 100)),
    over: pct > 100,
  };
}
